import React, { useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { FaInfoCircle, FaCheckCircle, FaQuestionCircle, FaLightbulb, FaShieldAlt, FaClock, FaChartPie, FaHeart, FaBalanceScale, FaUser, FaDog, FaBone, FaChartBar, FaStar, FaMagic, FaFlask, FaChartLine, FaHeartbeat, FaWeight, FaRuler, FaAdjust, FaPaw, FaSeedling, FaAppleAlt, FaCarrot, FaCalculator, FaLeaf, FaFire } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

// Register the required elements
ChartJS.register(ArcElement, Tooltip, Legend);

const RawDogFoodCalculator = () => {
  const [weight, setWeight] = useState('');
  const [age, setAge] = useState('');
  const [activity, setActivity] = useState('');
  const [proteinSource, setProteinSource] = useState('');
  const [vegetables, setVegetables] = useState('');
  const [result, setResult] = useState(null);

  const calculatePortion = () => {
    if (!weight || !age || !activity || !proteinSource || !vegetables) {
      alert('Please fill in all fields');
      return;
    }

    const weightNum = parseFloat(weight);
    let baseCalories = weightNum * 30 + 70; // Basic calculation for maintenance

    // Adjust based on age
    const ageFactors = {
      puppy: 1.5,
      adult: 1.2,
      senior: 0.9
    };

    // Adjust based on activity level
    const activityFactors = {
      low: 0.8,
      moderate: 1.0,
      high: 1.2
    };

    // Adjust based on protein source
    const proteinFactors = {
      chicken: 1.1,
      beef: 1.2,
      turkey: 1.15,
      fish: 1.25
    };

    const totalCalories = baseCalories * ageFactors[age] * activityFactors[activity] * proteinFactors[proteinSource];
    const dailyPortion = (totalCalories / 3500) * 1000; // Approximate conversion

    // Calculate macronutrient distribution
    const proteinGrams = dailyPortion * 0.25; // 25% protein
    const fatGrams = dailyPortion * 0.20; // 20% fat
    const carbsGrams = dailyPortion * 0.55; // 55% carbs

    // Calculate vegetable portion
    const vegPortion = dailyPortion * 0.15; // 15% vegetables

    setResult({
      calories: Math.round(totalCalories),
      portion: Math.round(dailyPortion),
      protein: proteinGrams,
      fat: fatGrams,
      carbs: carbsGrams,
      vegetables: vegPortion,
      proteinSource,
      vegetablesSelected: vegetables
    });
  };

  const chartData = result ? {
    labels: ['Protein', 'Fat', 'Carbs', 'Vegetables'],
    datasets: [
      {
        data: [result.protein, result.fat, result.carbs, result.vegetables],
        backgroundColor: ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0'],
        hoverBackgroundColor: ['#45A049', '#1976D2', '#F57C00', '#880E4F']
      }
    ]
  } : null;

  return (
    <>
      <Helmet>
        <title>Raw Dog Food Calculator - Calculate Proper Raw Food Portions</title>
        <meta name="description" content="Calculate proper raw dog food portions based on weight, age, activity level, and dietary preferences. Get accurate daily calorie needs and nutritional balance for your dog." />
        <meta name="keywords" content="raw dog food calculator, raw feeding calculator, dog raw diet calculator, raw food portion calculator, dog nutrition calculator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://your-website.com/raw-dog-food-calculator" />
        <meta property="og:title" content="Raw Dog Food Calculator - Calculate Proper Raw Food Portions" />
        <meta property="og:description" content="Calculate proper raw dog food portions based on weight, age, activity level, and dietary preferences. Get accurate daily calorie needs and nutritional balance for your dog." />
        <meta property="og:image" content="https://your-website.com/images/raw-dog-food-calculator-preview.png" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://your-website.com/raw-dog-food-calculator" />
        <meta property="twitter:title" content="Raw Dog Food Calculator - Calculate Proper Raw Food Portions" />
        <meta property="twitter:description" content="Calculate proper raw dog food portions based on weight, age, activity level, and dietary preferences. Get accurate daily calorie needs and nutritional balance for your dog." />
        <meta property="twitter:image" content="https://your-website.com/images/raw-dog-food-calculator-preview.png" />
      </Helmet>

      <div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-lg">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-green-600 mb-2">
            🐶 Raw Dog Food Calculator
          </h1>
          <p className="text-gray-600">
            Calculate proper raw food portions and nutritional balance for your dog
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dog's Weight (kg)
            </label>
            <input
              type="number"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder="Enter dog's weight"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Age
            </label>
            <select
              value={age}
              onChange={(e) => setAge(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Select age group</option>
              <option value="puppy">Puppy (0-1 year)</option>
              <option value="adult">Adult (1-7 years)</option>
              <option value="senior">Senior (7+ years)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Activity Level
            </label>
            <select
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Select activity level</option>
              <option value="low">Low</option>
              <option value="moderate">Moderate</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        <div className="space-y-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Protein Source
            </label>
            <select
              value={proteinSource}
              onChange={(e) => setProteinSource(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Select protein source</option>
              <option value="chicken">Chicken</option>
              <option value="beef">Beef</option>
              <option value="turkey">Turkey</option>
              <option value="fish">Fish</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Vegetables
            </label>
            <select
              value={vegetables}
              onChange={(e) => setVegetables(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Select vegetables</option>
              <option value="carrots">Carrots</option>
              <option value="sweet-potato">Sweet Potato</option>
              <option value="broccoli">Broccoli</option>
            </select>
          </div>
        </div>

        <button
          onClick={calculatePortion}
          className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
        >
          Calculate Raw Food Portion
        </button>

        {result && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold text-green-600 mb-4">Results</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Daily Recommendations Card */}
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                    <FaChartBar className="text-green-600 w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold">Daily Recommendations</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <FaFire className="text-green-600 w-5 h-5" />
                    <div>
                      <p className="text-gray-700">Daily Calorie Needs:</p>
                      <p className="text-2xl font-bold text-green-600">{result.calories} calories</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaWeight className="text-green-600 w-5 h-5" />
                    <div>
                      <p className="text-gray-700">Daily Food Portion:</p>
                      <p className="text-2xl font-bold text-green-600">{result.portion} grams</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaLeaf className="text-green-600 w-5 h-5" />
                    <div>
                      <p className="text-gray-700">Daily Vegetable Portion:</p>
                      <p className="text-2xl font-bold text-green-600">{result.vegetables} grams</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Nutritional Breakdown Card */}
              <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                    <FaChartPie className="text-green-600 w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-semibold">Nutritional Breakdown</h3>
                </div>
                <div className="space-y-6">
                  <div className="w-full h-[300px]">
                    <Pie data={chartData} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <FaBone className="text-green-600 w-5 h-5" />
                        <h4 className="font-medium">Protein Source</h4>
                      </div>
                      <p className="font-medium">{result.proteinSource.charAt(0).toUpperCase() + result.proteinSource.slice(1)}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <FaCarrot className="text-green-600 w-5 h-5" />
                        <h4 className="font-medium">Vegetables</h4>
                      </div>
                      <p className="font-medium">{result.vegetablesSelected.charAt(0).toUpperCase() + result.vegetablesSelected.slice(1)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold mb-4">FAQs</h2>
          <div className="space-y-4">
            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-semibold">What is raw feeding?</h3>
              <p className="text-gray-600">
                Raw feeding involves feeding dogs a diet of raw, unprocessed meats, bones, and vegetables that mimics their natural diet in the wild.
              </p>
            </div>
            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-semibold">Is raw feeding safe for dogs?</h3>
              <p className="text-gray-600">
                When done properly under veterinary guidance, raw feeding can be safe and beneficial. It's essential to ensure proper food handling and balanced nutrition.
              </p>
            </div>
            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-semibold">How do I transition my dog to raw food?</h3>
              <p className="text-gray-600">
                Gradually transition over 7-10 days by mixing increasing amounts of raw food with their current diet to allow their digestive system to adapt.
              </p>
            </div>
            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-semibold">What are the benefits of raw feeding?</h3>
              <p className="text-gray-600">
                Benefits include improved digestion, shinier coat, cleaner teeth, and better overall health. However, it's essential to maintain a balanced diet.
              </p>
            </div>
            <div className="border-l-4 border-green-600 pl-4">
              <h3 className="font-semibold">How often should I feed my dog raw food?</h3>
              <p className="text-gray-600">
                Generally, adult dogs should be fed twice a day. Puppies may require more frequent feedings. Adjust based on your dog's age and activity level.
              </p>
            </div>
          </div>
        </div>

        {/* How to Use Section */}
        <div className="mt-8 bg-gray-50 p-4 rounded-lg mb-8">
          <h2 className="text-xl font-semibold mb-4">How to Use</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Step 1 Card */}
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                  <FaUser className="text-green-600 w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold">Step 1: Enter Dog's Information</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <FaWeight className="text-green-600 w-5 h-5" />
                  <div>
                    <p className="text-gray-700">Input your dog's weight in kilograms</p>
                    <p className="text-sm text-gray-500">This is crucial for accurate calculations</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <FaDog className="text-green-600 w-5 h-5" />
                  <div>
                    <p className="text-gray-700">Select your dog's age group</p>
                    <p className="text-sm text-gray-500">Choose from Puppy (0-1 year), Adult (1-7 years), or Senior (7+ years)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <FaRuler className="text-green-600 w-5 h-5" />
                  <div>
                    <p className="text-gray-700">Choose your dog's activity level</p>
                    <p className="text-sm text-gray-500">Select Low, Moderate, or High based on your dog's daily activity</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 Card */}
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                  <FaBone className="text-green-600 w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold">Step 2: Select Dietary Preferences</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <FaPaw className="text-green-600 w-5 h-5" />
                  <div>
                    <p className="text-gray-700">Choose your preferred protein source</p>
                    <p className="text-sm text-gray-500">Options include Chicken, Beef, Turkey, and Fish</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <FaSeedling className="text-green-600 w-5 h-5" />
                  <div>
                    <p className="text-gray-700">Select preferred vegetables</p>
                    <p className="text-sm text-gray-500">Choose from Carrots, Sweet Potato, and Broccoli</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 Card */}
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                  <FaChartBar className="text-green-600 w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold">Step 3: Calculate</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <FaCalculator className="text-green-600 w-5 h-5" />
                  <div>
                    <p className="text-gray-700">Click the "Calculate Raw Food Portion" button</p>
                    <p className="text-sm text-gray-500">The calculator will process your inputs and show results</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <FaChartPie className="text-green-600 w-5 h-5" />
                  <div>
                    <p className="text-gray-700">Review the results section</p>
                    <p className="text-sm text-gray-500">Check daily calorie needs, food portion recommendations, and nutritional breakdown</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Notes Card */}
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                  <FaShieldAlt className="text-green-600 w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold">Important Notes</h3>
              </div>
              <div className="space-y-4">
                <div className="bg-green-50 p-3 rounded-lg flex items-center">
                  <FaInfoCircle className="text-green-600 w-5 h-5 mr-3" />
                  <div>
                    <p className="text-gray-700">Consult with your veterinarian</p>
                    <p className="text-sm text-gray-500">Always consult your vet before making significant changes to your dog's diet</p>
                  </div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg flex items-center">
                  <FaClock className="text-green-600 w-5 h-5 mr-3" />
                  <div>
                    <p className="text-gray-700">Start slow with raw feeding</p>
                    <p className="text-sm text-gray-500">Gradually transition your dog to raw food over 7-10 days</p>
                  </div>
                </div>
                <div className="bg-green-50 p-3 rounded-lg flex items-center">
                  <FaHeartbeat className="text-green-600 w-5 h-5 mr-3" />
                  <div>
                    <p className="text-gray-700">Monitor your dog's health</p>
                    <p className="text-sm text-gray-500">Watch for any changes in stool, energy levels, or coat condition</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features & Benefits Section */}
        <div className="mt-8 bg-gray-50 p-4 rounded-lg mb-8">
          <h2 className="text-xl font-semibold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Features Card */}
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <h3 className="text-lg font-semibold mb-4">Key Features</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <FaMagic className="text-green-600 w-6 h-6" />
                  <div>
                    <h4 className="font-medium text-gray-700">Customizable Calculations</h4>
                    <p className="text-sm text-gray-500">Tailored calculations based on weight, age, and activity level</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <FaFlask className="text-green-600 w-6 h-6" />
                  <div>
                    <h4 className="font-medium text-gray-700">Dietary Preferences</h4>
                    <p className="text-sm text-gray-500">Choose from multiple protein sources and vegetables</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <FaChartLine className="text-green-600 w-6 h-6" />
                  <div>
                    <h4 className="font-medium text-gray-700">Visual Analytics</h4>
                    <p className="text-sm text-gray-500">Interactive pie chart showing nutritional breakdown</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <FaAdjust className="text-green-600 w-6 h-6" />
                  <div>
                    <h4 className="font-medium text-gray-700">Life Stage Customization</h4>
                    <p className="text-sm text-gray-500">Specific calculations for puppies, adults, and seniors</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits Card */}
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <h3 className="text-lg font-semibold mb-4">Benefits</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <FaLeaf className="text-green-600 w-6 h-6" />
                  <div>
                    <h4 className="font-medium text-gray-700">Natural Nutrition</h4>
                    <p className="text-sm text-gray-500">Calculate balanced raw food portions for your dog's natural diet</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <FaHeart className="text-green-600 w-6 h-6" />
                  <div>
                    <h4 className="font-medium text-gray-700">Health Benefits</h4>
                    <p className="text-sm text-gray-500">Supports better digestion, shinier coat, and overall health</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <FaBalanceScale className="text-green-600 w-6 h-6" />
                  <div>
                    <h4 className="font-medium text-gray-700">Weight Management</h4>
                    <p className="text-sm text-gray-500">Supports healthy weight maintenance through proper portion control</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <FaStar className="text-green-600 w-6 h-6" />
                  <div>
                    <h4 className="font-medium text-gray-700">Customizable Care</h4>
                    <p className="text-sm text-gray-500">Tailored recommendations for your dog's unique needs and lifestyle</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Features Card */}
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
              <h3 className="text-lg font-semibold mb-4">Additional Features</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <FaAppleAlt className="text-green-600 w-6 h-6" />
                  <div>
                    <h4 className="font-medium text-gray-700">Nutrient Tracking</h4>
                    <p className="text-sm text-gray-500">Track your dog's daily nutrient intake and requirements</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <FaCarrot className="text-green-600 w-6 h-6" />
                  <div>
                    <h4 className="font-medium text-gray-700">Vegetable Recommendations</h4>
                    <p className="text-sm text-gray-500">Get suggestions for safe and nutritious vegetables</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <FaSeedling className="text-green-600 w-6 h-6" />
                  <div>
                    <h4 className="font-medium text-gray-700">Seasonal Adjustments</h4>
                    <p className="text-sm text-gray-500">Adjust portions based on seasonal changes and activity levels</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RawDogFoodCalculator;
